import { FC, useEffect } from 'react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { SubscriptionHeaderTitle } from 'src/components/AzarOnly/Subscription/styles';
import SubscriptionDuplicateModal from 'src/components/AzarOnly/Subscription/SubscriptionDuplicateModal';
import { SubscriptionGuideLandFrom } from 'src/components/AzarOnly/Subscription/SubscriptionGuideModal';
import SubscriptionPurchaseButton from 'src/components/AzarOnly/Subscription/SubscriptionPurchaseButton';
import type { PriceInfo } from 'src/components/AzarOnly/Subscription/types';
import { getPaymentButtonTextKey } from 'src/components/AzarOnly/Subscription/utils';
import type { PurchasablePlan } from 'src/components/AzarOnly/Subscription/utils/formatSubscription';
import type { PaymentAction } from 'src/components/AzarOnly/Subscription/utils/getPaymentButtonTextKey';
import ModalLayoutBase, { MobileModalType, ModalContent, ModalHeader } from 'src/components/ModalLayout';
import ModalFooter from 'src/components/ModalLayout/ModalFooter';
import { useSubscriptionEventData } from 'src/hooks/useSubscription';
import { useCreateCheckout, useManagementSession } from 'src/hooks/useSubscription';
import { useSubscriptionStatus } from 'src/hooks/useSubscription/useSubscriptionStatus';
import { showModalAtom } from 'src/stores/modal/atoms';
import { checkoutEventContextAtom, checkoutSubscriptionIdAtom } from 'src/stores/subscription/atoms';
import { showCommonErrorToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import PlanCard from './PlanCard';
import SubscriptionTerms from './SubscriptionTerms';
interface Props {
  pageId: string;
  /** 유저가 처음 진입한 페이지 ID. 탭 전환과 무관하게 고정되어 애널리틱스 연속성을 보장한다. */
  initialPageId?: string;
  modalTitle: string;
  planModule: PurchasablePlan['planModule'];
  pgIntroductoryOfferEligible: boolean;
  termsTexts: string[] | undefined;
  action: PaymentAction;
  priceInfo: PriceInfo;
  productId: string;
  subscriptionId: string | undefined;
  planId: string;
  onClose?: () => void;
  landFrom: SubscriptionGuideLandFrom;
  initialFeature?: string;
}
const ModalLayout = styled(ModalLayoutBase)`
  width: 458px;
`;
const SubscriptionPaymentModal: FC<Props> = ({
  pageId,
  initialPageId,
  landFrom,
  initialFeature,
  modalTitle,
  planModule,
  pgIntroductoryOfferEligible,
  termsTexts,
  action,
  priceInfo,
  productId,
  subscriptionId,
  planId,
  onClose
}) => {
  const {
    t
  } = useTranslation();
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const showModal = useSetAtom(showModalAtom);
  const subscriptionEventData = useSubscriptionEventData({
    pageId
  });
  const setCheckoutSubscriptionId = useSetAtom(checkoutSubscriptionIdAtom);
  const setCheckoutEventContext = useSetAtom(checkoutEventContextAtom);
  const {
    hasAppSubscription,
    hasWebCoreSubscription,
    hasWebMaxSubscription
  } = useSubscriptionStatus();
  const {
    mutate: createCheckout,
    isPending: isCheckoutPending
  } = useCreateCheckout({
    onSuccess: ({
      subscriptionId: newSubscriptionId,
      url
    }) => {
      setCheckoutSubscriptionId(newSubscriptionId);
      window.location.href = url;
    },
    onError: error => {
      Sentry.captureException(error, {
        tags: {
          feature: 'subscription_checkout'
        }
      });
      showCommonErrorToast();
    }
  });
  const {
    mutate: createManagementSession,
    isPending: isManagementPending
  } = useManagementSession();
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SUBSCRIPTION,
      eventName: EVENT_NAME.SUBS_PLAN__IMPRESSION,
      eventParams: {
        button_type: subscriptionEventData.buttonType,
        current_page_id: pageId,
        initial_feature: initialFeature ?? 'none',
        initial_page_id: initialPageId ?? pageId,
        land_from: landFrom,
        ...subscriptionEventData.eventParams,
        is_store_account_active: false,
        is_store_account_matched: false
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isPending = isCheckoutPending || isManagementPending;
  const handleClick = () => {
    const isIntroPriceDisplayed = priceInfo?.formattedIntroductoryPrice != null;
    pushEvent({
      eventType: EVENT_TYPE.SUBSCRIPTION,
      eventName: EVENT_NAME.SUBS_PLAN__CLICK_BUTTON,
      eventParams: {
        button_type: subscriptionEventData.buttonType,
        current_page_id: pageId,
        initial_feature: initialFeature ?? 'none',
        initial_page_id: initialPageId ?? pageId,
        is_intro_price_displayed: isIntroPriceDisplayed,
        land_from: landFrom,
        ...subscriptionEventData.eventParams,
        is_store_account_active: false,
        is_store_account_matched: false
      }
    });
    setCheckoutEventContext({
      button_type: subscriptionEventData.buttonType,
      current_page_id: pageId,
      initial_feature: initialFeature ?? 'none',
      initial_page_id: initialPageId ?? pageId,
      is_intro_price_displayed: isIntroPriceDisplayed,
      land_from: landFrom,
      product_id: productId
    });
    if (action === 'new') {
      // 이미 구독 중인 경우 중복 구독 모달을 표시한다.
      const hasDuplicateSubscription = hasAppSubscription || hasWebCoreSubscription || hasWebMaxSubscription;
      if (hasDuplicateSubscription) {
        showModal({
          key: ModalType.SUBSCRIPTION_DUPLICATE,
          component: () => <SubscriptionDuplicateModal />
        });
        return;
      }
      createCheckout(productId);
    } else if (subscriptionId) {
      createManagementSession(() => subscriptionId);
    } else {
      const error = new Error(`Missing subscriptionId for ${action} action`);
      Sentry.captureException(error, {
        tags: {
          feature: 'subscription_checkout'
        },
        extra: {
          action,
          productId
        }
      });
      showCommonErrorToast();
    }
  };
  const hasIntroductoryPrice = pgIntroductoryOfferEligible && !!planModule.introductoryPgPrice;
  const textKey = getPaymentButtonTextKey(action, planId, hasIntroductoryPrice);
  return <ModalLayout isDim mobileType={MobileModalType.BOTTOM_SHEET} navButtonType={ModalNavButtonType.NONE} onClose={onClose} data-sentry-element="ModalLayout" data-sentry-component="SubscriptionPaymentModal" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <SubscriptionHeaderTitle data-sentry-element="SubscriptionHeaderTitle" data-sentry-source-file="index.tsx">{modalTitle}</SubscriptionHeaderTitle>
      </ModalHeader>

      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <PlanCard planModule={planModule} pgIntroductoryOfferEligible={pgIntroductoryOfferEligible} data-sentry-element="PlanCard" data-sentry-source-file="index.tsx" />
        <SubscriptionTerms texts={termsTexts} data-sentry-element="SubscriptionTerms" data-sentry-source-file="index.tsx" />
      </ModalContent>

      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <SubscriptionPurchaseButton priceInfo={priceInfo} onClick={handleClick} disabled={isPending} loading={isPending} data-sentry-element="SubscriptionPurchaseButton" data-sentry-source-file="index.tsx">
          {t(textKey)}
        </SubscriptionPurchaseButton>
      </ModalFooter>
    </ModalLayout>;
};
export default SubscriptionPaymentModal;